import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Users, Building2, CalendarDays, Coins, MapPin, Loader2, ShieldCheck, Clock, Activity, TrendingUp, ChevronRight } from "lucide-react";

const statusStyle = { CONFIRMED: "status-confirmed", PENDING_CASH: "status-pending", PENDING_PAYMENT: "status-pending", CANCELLED: "status-cancelled" };

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [venueApprovalCounts, setVenueApprovalCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/admin/dashboard"),
      api.get("/vov/admin/queue").catch(() => null),
    ]).then(([dashboardRes, venueApprovalRes]) => {
      setData(dashboardRes.data);
      setVenueApprovalCounts(venueApprovalRes?.data?.counts || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  if (!data) return <div className="text-center py-20 font-heading text-xl">Unable to load dashboard</div>;

  const venueApprovalPending = (venueApprovalCounts.pending || 0) + (venueApprovalCounts.under_review || 0);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="admin-dashboard">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="w-7 h-7 text-court-orange" />
        <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">Platform Admin</h1>
      </div>
      <p className="text-muted-foreground mb-8">System-wide overview and management</p>

      {/* Top Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-8">
        {[
          { label: "Users", value: data.total_users, icon: Users, color: "text-blue-500", click: () => navigate("/admin/users") },
          { label: "Providers", value: data.total_providers, icon: Building2, color: "text-purple-500", click: () => navigate("/admin/providers") },
          { label: "Venues", value: data.total_venues, icon: MapPin, color: "text-lime-volt", click: () => navigate("/admin/venues") },
          { label: "Bookings", value: data.total_bookings, icon: CalendarDays, color: "text-court-orange", click: () => navigate("/admin/bookings") },
          { label: "GMV", value: `₱${data.gmv?.toLocaleString()}`, icon: TrendingUp, color: "text-green-500" },
          { label: "Platform Fees", value: `₱${data.platform_fees?.toLocaleString()}`, icon: Coins, color: "text-amber-500" },
        ].map((s, i) => (
          <div key={i} onClick={s.click} data-testid={`admin-stat-${i}`}
            className={`bg-white border-2 border-slate-100 p-4 ${s.click ? 'cursor-pointer hover:border-slate-200 hover:-translate-y-0.5' : ''} transition-all`}>
            <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
            <p className="font-heading text-xl md:text-2xl font-bold">{s.value}</p>
            <p className="text-[10px] font-heading uppercase tracking-[0.15em] text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => navigate("/admin/venue-verification")}
        className="w-full mb-8 bg-white border-2 border-slate-100 p-5 text-left hover:border-court-orange/40 hover:-translate-y-0.5 transition-all"
        data-testid="venue-approval-card"
      >
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-full bg-court-orange/10 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-court-orange" />
              </div>
              <div>
                <h2 className="font-heading text-xl uppercase tracking-tight font-bold">SP / Venue Approval</h2>
                <p className="text-sm text-muted-foreground">Review venue submissions, documents, duplicate risks, and ownership claims.</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 md:justify-end">
            <Badge className={venueApprovalPending > 0 ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}>
              {venueApprovalPending > 0 ? `${venueApprovalPending} pending` : "Up to date"}
            </Badge>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </div>
        </div>
      </button>

      {/* Breakdown Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Users by Role */}
        <div className="bg-white border-2 border-slate-100 p-5" data-testid="users-breakdown">
          <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" />Users by Role
          </h3>
          <div className="space-y-2">
            {Object.entries(data.users_by_role || {}).map(([role, count]) => (
              <div key={role} className="flex justify-between items-center text-sm">
                <span className="capitalize">{role.replace("_", " ").toLowerCase()}</span>
                <span className="font-heading font-bold">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Providers by Status */}
        <div className="bg-white border-2 border-slate-100 p-5" data-testid="providers-breakdown">
          <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4" />Providers by Status
          </h3>
          <div className="space-y-2">
            {Object.entries(data.providers_by_status || {}).filter(([_, c]) => c > 0).map(([status, count]) => (
              <div key={status} className="flex justify-between items-center text-sm">
                <Badge variant="outline" className="text-[10px] font-heading uppercase">{status.replace("_", " ")}</Badge>
                <span className="font-heading font-bold">{count}</span>
              </div>
            ))}
            {Object.values(data.providers_by_status || {}).every(c => c === 0) && (
              <p className="text-sm text-muted-foreground">No providers yet</p>
            )}
          </div>
        </div>

        {/* Bookings by Status */}
        <div className="bg-white border-2 border-slate-100 p-5" data-testid="bookings-breakdown">
          <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-3 flex items-center gap-2">
            <CalendarDays className="w-4 h-4" />Bookings by Status
          </h3>
          <div className="space-y-2">
            {Object.entries(data.bookings_by_status || {}).filter(([_, c]) => c > 0).map(([status, count]) => (
              <div key={status} className="flex justify-between items-center text-sm">
                <Badge variant="outline" className={`text-[10px] font-heading uppercase ${statusStyle[status] || ''}`}>{status.replace("_", " ")}</Badge>
                <span className="font-heading font-bold">{count}</span>
              </div>
            ))}
            {Object.values(data.bookings_by_status || {}).every(c => c === 0) && (
              <p className="text-sm text-muted-foreground">No bookings yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div data-testid="recent-bookings-admin">
          <h3 className="font-heading text-lg uppercase tracking-tight font-bold mb-3 flex items-center gap-2">
            <Activity className="w-5 h-5 text-court-orange" />Recent Bookings
          </h3>
          <div className="space-y-2">
            {data.recent_bookings?.map(b => (
              <div key={b.booking_id} className="bg-white border border-slate-100 p-3 flex items-center gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{b.user_name || b.user_email}</p>
                  <p className="text-xs text-muted-foreground truncate">{b.resource_name} - {b.venue_name}</p>
                </div>
                <Badge className={`${statusStyle[b.status]} text-[10px] font-heading uppercase shrink-0`}>{b.status?.replace("_"," ")}</Badge>
                <span className="font-heading font-bold text-court-orange whitespace-nowrap">₱{b.total_amount?.toFixed(0)}</span>
              </div>
            ))}
          </div>
        </div>

        <div data-testid="recent-users-admin">
          <h3 className="font-heading text-lg uppercase tracking-tight font-bold mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-court-orange" />Recent Users
          </h3>
          <div className="space-y-2">
            {data.recent_users?.map(u => (
              <div key={u.user_id} className="bg-white border border-slate-100 p-3 flex items-center gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{u.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                </div>
                <Badge variant="outline" className="text-[10px] font-heading uppercase shrink-0">{u.role?.replace("_"," ")}</Badge>
                <span className="text-[10px] text-muted-foreground whitespace-nowrap">{u.created_at?.slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
